import {
  AllCommunityModule,
  themeQuartz,
  type ColDef,
  type ColumnMovedEvent,
  type ColumnVisibleEvent,
  type GetRowIdParams,
  type ICellRendererParams,
  type IsFullWidthRowParams,
  type OverlayType,
  type RowClassParams,
  type RowSelectedEvent,
  type SelectionChangedEvent,
  type SelectionEventSourceType,
  type SortChangedEvent,
} from "ag-grid-community";
import { AgGridProvider, AgGridReact } from "ag-grid-react";
import classNames from "classnames";
import * as React from "react";

import { colId } from "./buildColumnsForPreferences";
import cellStyles from "./cellStyles.module.css";
import styles from "./DataTable.module.css";
import { ExpandableDetailPanel } from "./internal/ExpandableDetailPanel";
import { ExpandChevron } from "./internal/ExpandChevron";
import { GridRow } from "./internal/gridRow";
import { stepToward, type LoadingPhase } from "./internal/loadingPhase";
import { PaginationFooter } from "./internal/PaginationFooter";
import { SkeletonCell } from "./internal/SkeletonCell";
import {
  useRowExpansion,
  type ExpandableGridRow,
} from "./internal/useRowExpansion";
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  type DataTableHandle,
  type DataTableLoadingState,
  type DataTableState,
} from "./useTable";

const AG_GRID_MODULES = [AllCommunityModule];

const SUPPRESSED_OVERLAYS: OverlayType[] = [
  "noRows",
  "loading",
  "noMatchingRows",
];

const DEFAULT_COL_DEF = {
  sortable: false,
  resizable: true,
  lockPinned: true,
  /**
   * Default cell typography for plain-text values (secondary content: smaller,
   * muted). A column that sets its own `cellClass` replaces this.
   */
  cellClass: cellStyles.secondaryContent,
  /**
   * Column visibility is fully driven by preferences + user actions
   */
  hide: true,
  /**
   * The server is the sort authority. This `comparator: () => 0` prevents AG Grid
   * from client-side reordering when a consumer column opts into `sortable: true`.
   * Without it, the displayed order would diverge from the server's ordering.
   */
  comparator: () => 0,
} satisfies ColDef;

const ROW_SELECTION_BASE = {
  mode: "multiRow",
  selectAll: "currentPage",
} as const;

const SELECTION_COLUMN_DEF = {
  suppressMovable: true,
  lockPosition: "left",
  lockPinned: true,
  flex: 0,
  width: 48,
  suppressSizeToFit: true,
} as const;

const SELECTION_COLUMN_ID = "ag-Grid-SelectionColumn";
const EXPAND_COLUMN_ID = "__expand";

const INTERNAL_COLUMN_IDS = new Set([SELECTION_COLUMN_ID, EXPAND_COLUMN_ID]);

// AG Grid's selection-change events carry a `source` discriminator
// (analogous to `uiColumnSorted` etc.). We route each user-initiated
// source to whichever event channel handles it best:
//
// - Per-row sources fire `rowSelected` once per affected row. We use those
//   for single-row clicks so we don't pay an O(n) map copy on every
//   member of a 100-row bulk select.
// - Bulk sources fire `selectionChanged` once with a post-change snapshot;
//   we handle those there and use `api.forEachNode` to recover the set of
//   ids the bulk op actually touched.
// - Everything else (programmatic api calls, row-data refresh, init,
//   group changes) is internal noise that would otherwise clobber the
//   cross-page accumulator. Ignored on both channels.
const PER_ROW_SELECTION_SOURCES: ReadonlySet<SelectionEventSourceType> =
  new Set(["checkboxSelected", "rowClicked", "spaceKey"]);

const BULK_SELECTION_SOURCES: ReadonlySet<SelectionEventSourceType> = new Set([
  "uiSelectAll",
  "uiSelectAllFiltered",
  "uiSelectAllCurrentPage",
  "keyboardSelectAll",
]);

function detailPanelId(rowId: string): string {
  return `data-table-detail-${rowId}`;
}

export interface DataTableProps<TData> {
  /**
   * Table instance from `useTable<TData>(…)` (or `useResourceView<TData>(…).table`).
   * Carries `getRowId`, the mount-snapshot initial sort / visible columns,
   * the imperative handle ref slot, the cross-page selection accumulator,
   * and the grid → state mirror callbacks. AG Grid is wired through this
   * one prop instead of a dozen.
   */
  table: DataTableState<TData>;
  /**
   * Whether a page exists after the current `currentPage`
   */
  hasNextPage: boolean;
  /**
   * What the table is still waiting on. Any value but `"none"` renders skeleton
   * rows and disables the next-page button, so a fetch can't be queued behind
   * another.
   *
   * @default "none"
   */
  loading?: DataTableLoadingState;
  /**
   * Column definitions. AG Grid `ColDef` is the public contract — pass raw
   * `ColDef` for full access to AG Grid features, or use `createColumnHelper`
   * for type-safe shortcuts on the common shapes (field accessor, computed
   * value getter, display-only).
   */
  columns: ColDef<TData>[];
  /**
   * Column defaults applied to every column in this grid, merged over the
   * primitive's built-in defaults.
   *
   * Pass a stable reference (module const) to avoid
   * re-defining columns on every render.
   */
  defaultColDef?: ColDef<TData>;
  data: TData[];
  noRowsContent: React.ReactNode;
  /**
   * Total rows across all pages, surfaced in the pagination footer's
   * `1-25 of N` label. When `undefined`, the count label is hidden.
   */
  totalRowCount?: number | undefined;
  /**
   * Show the row-selection checkbox column. Defaults to `false`. Selection
   * state itself lives on `table.selection` / `table.selectionMap` — this
   * prop only gates the UI affordance.
   */
  selectionEnabled?: boolean;
  /**
   * Row expansion config. Pass an object to enable per-row detail panels —
   * `renderRow` is invoked with the row data and its return value is rendered
   * inside the expanded panel. Omit to disable expansion entirely.
   *
   * `initialExpandedIds` seeds which rows start expanded (by row id) — used
   * for deep links that land on one row with its panel already open. Captured
   * once at mount.
   */
  expansion?: {
    renderRow: (row: TData) => React.ReactNode;
    initialExpandedIds?: ReadonlySet<string>;
  };
  className?: string;
  /** Accessible name for the table. When given, the grid is a region with this name. */
  ariaLabel?: string;
  /**
   * Leave out the pagination footer for a short in-memory list, where "1-3 of
   * 3" and a page-size picker are noise. Short means `totalRowCount` fits in
   * the smallest page size the footer offers, so changing the page size never
   * hides the footer; without a `totalRowCount` the footer always shows.
   *
   * @default false
   */
  hidePaginationWhenShort?: boolean;
}

/**
 * Paginated, sortable, selectable AG Grid wrapper. The full table-state
 * surface lives on the `table` instance; pass it through and the component
 * wires AG Grid up to it. Pair with `<ResourceView.Root>` for filter UI:
 *
 * @example
 * ```tsx
 * const table = useTable<MyRow>({ getRowId: (r) => r.id });
 * return (
 *   <ResourceView.Root>
 *     <DataTable<MyRow>
 *       table={table}
 *       data={rows}
 *       columns={COLUMNS}
 *       hasNextPage={query.hasNextPage}
 *       noRowsContent="No rows"
 *     />
 *   </ResourceView.Root>
 * );
 * ```
 */
export function DataTable<TData>(props: DataTableProps<TData>) {
  const {
    table,
    hasNextPage,
    loading = "none",
    columns,
    defaultColDef,
    data,
    noRowsContent,
    totalRowCount,
    selectionEnabled = false,
    expansion,
    className,
    ariaLabel,
    hidePaginationWhenShort = false,
  } = props;

  // Merge the consumer's column defaults over the primitive's built-in ones.
  // Cast to the internal row type (the grid wraps rows in `ExpandableGridRow`),
  // same as the `columns` prop below. Memoized on the prop identity so AG Grid
  // doesn't re-define columns each render — consumers pass a stable
  // (module-const) `defaultColDef`.
  const mergedDefaultColDef = React.useMemo(
    () =>
      ({ ...DEFAULT_COL_DEF, ...defaultColDef }) as ColDef<
        ExpandableGridRow<TData>
      >,
    [defaultColDef],
  );

  // Destructure off the instance. These are stable identities — readers
  // get re-memoized on state changes; methods come from `useCallback` so
  // their identity tracks their dep list.
  const {
    initialSort,
    sort,
    setSort,
    onVisibleColumnChange,
    visibleColumnIds,
    initialVisibleColumnIds,
    onColumnOrderChange,
    onRowSelectionChange,
    onBulkSelectionChange,
    selectionMap,
    pageSize,
    setPageSize,
    currentPage,
    nextPage,
    prevPage,
    canGetPrevPage,
    getRowId,
    handleRef: tableRef,
  } = table;

  const gridRef = React.useRef<AgGridReact<ExpandableGridRow<TData>>>(null);

  // Push `table.sort` into AG Grid's column model whenever it changes so
  // it displays the sort state in the column.
  React.useEffect(
    function showColumnSorted() {
      const api = gridRef.current?.api;
      if (!api) {
        return;
      }
      api.applyColumnState({
        state: sort ? [{ colId: sort.columnId, sort: sort.direction }] : [],
        defaultState: { sort: null },
      });
    },
    [sort],
  );

  React.useImperativeHandle(tableRef, (): DataTableHandle => ({
    deselectAll: () => gridRef.current?.api?.deselectAll(),
    setColumnVisibility: (id, visible) => {
      gridRef.current?.api?.setColumnsVisible([id], visible);
    },
    setColumnOrder: (columnIds) => {
      gridRef.current?.api?.applyColumnState({
        state: columnIds.map((id) => ({ colId: id })),
        applyOrder: true,
      });
    },
    refreshCells: (columnIds) => {
      gridRef.current?.api?.refreshCells(
        columnIds ? { columns: columnIds, force: true } : { force: true },
      );
    },
  }));

  const showExpansion = expansion !== undefined;
  const renderExpandedRow = expansion?.renderRow;

  // `getRowId` is a pure row→id projection; capture the first reference so
  // AG Grid sees one identity strategy. Later changes are intentionally ignored
  // — if a consumer needs a different identity strategy, remount the table.
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const stableGetRowId = React.useMemo(() => getRowId, []);

  // Snapshot of initial sort, captured once at mount. AG Grid reads
  // `initialState` only on first render, so this never re-applies. Initial column
  // order comes from the consumer's ColDef array order, and visibility from ColDef
  // `hide` — reconciled against the user's toggles by `reconcileVisibility`
  // below — so neither needs an `initialState` slot here.
  const [initialGridState] = React.useState(() => {
    if (!initialSort) {
      return undefined;
    }
    return {
      sort: {
        sortModel: [
          { colId: initialSort.columnId, sort: initialSort.direction },
        ],
      },
      partialColumnState: true,
    };
  });

  const { displayRows, toggle: toggleExpansion } = useRowExpansion<TData>({
    data,
    getRowId: stableGetRowId,
    initialExpandedIds: expansion?.initialExpandedIds,
  });

  const showSkeleton = loading !== "none";
  const isLoading = loading !== "none";

  // Placeholder rows, one per `pageSize`. Row virtualization keeps a large
  // page cheap.
  const skeletonRows = React.useMemo<ExpandableGridRow<TData>[]>(
    () =>
      Array.from(
        { length: pageSize },
        (_, i) =>
          ({
            __skeleton: true,
            id: `__skeleton_${i}`,
          }) as unknown as ExpandableGridRow<TData>,
      ),
    [pageSize],
  );

  const loadedRows: ExpandableGridRow<TData>[] = showExpansion
    ? displayRows
    : (data as ExpandableGridRow<TData>[]);

  // How far the grid has been walked toward the loaded content. Holding the
  // arrays themselves in state would re-enter the effect below on every fresh
  // `data` identity a consumer passes, and loop.
  const [phase, setPhase] = React.useState<LoadingPhase>(
    showSkeleton ? "skeleton" : "loaded",
  );
  const columnsAreLoaded = phase === "loaded";

  const wrappedGetRowId = React.useCallback(
    (params: GetRowIdParams<ExpandableGridRow<TData>>) => {
      if (params.data.__skeleton === true) {
        // Synthetic id assigned above — placeholder rows have no real TData.
        return (params.data as unknown as { id: string }).id;
      }
      const id = stableGetRowId(params.data);
      return params.data.__detail === true ? `${id}__detail` : id;
    },
    [stableGetRowId],
  );

  const finalColumns = React.useMemo<ColDef<ExpandableGridRow<TData>>[]>(() => {
    /**
     * This is safe because detail rows are intercepted by
     * `isFullWidthRow` and rendered via `fullWidthCellRenderer` below — they
     * never reach the per-column `cellRenderer` / `valueGetter` / etc., so
     * `params.data` in consumer callbacks is always a real `TData`.
     *
     * Considered wrapping TData with ExpandableGridRow but every Ag-grid call site
     * would be impacted so left it be.
     */
    const consumerCols = columns as ColDef<ExpandableGridRow<TData>>[];

    if (!showExpansion) {
      return consumerCols;
    }
    const expandColumn: ColDef<ExpandableGridRow<TData>> = {
      colId: EXPAND_COLUMN_ID,
      flex: 0,
      width: 40,
      suppressSizeToFit: true,
      pinned: "left",
      sortable: false,
      resizable: false,
      suppressMovable: true,
      lockPosition: "left",
      // Expansion column should never be hidden
      hide: false,
      cellClass: styles.expandCell,
      headerClass: styles.expandHeaderCell,
      onCellClicked: (event) => {
        const row = event.data;
        if (!row || row.__detail) {
          return;
        }
        const rowId = stableGetRowId(row);
        toggleExpansion(rowId);
      },
      // The cell renders a native <button>, so Enter/Space already trigger the
      // toggle via the button's activation → onCellClicked. Suppress AG Grid's
      // own Enter/Space handling so it doesn't fire a second toggle.
      suppressKeyboardEvent: (params) =>
        params.event.key === "Enter" || params.event.key === " ",
      valueGetter: (params) => params.data?.__expanded ?? false,
      cellRenderer: (params: ICellRendererParams<ExpandableGridRow<TData>>) => {
        const row = params.data;
        if (!row || row.__detail) {
          return null;
        }
        const isExpanded = Boolean(params.value);
        const rowId = stableGetRowId(row);
        return (
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={detailPanelId(rowId)}
            aria-label={isExpanded ? "Collapse row" : "Expand row"}
            className={styles.expandButton}
          >
            <ExpandChevron isExpanded={isExpanded} />
          </button>
        );
      },
    };
    return [expandColumn, ...consumerCols];
  }, [columns, showExpansion, toggleExpansion, stableGetRowId]);

  // Skeleton variant of `finalColumns`: identical geometry, with data access
  // and interaction stripped, so swapping between the two never shifts the
  // header or the column widths.
  const skeletonColumns = React.useMemo<ColDef<GridRow<TData>>[]>(
    () =>
      finalColumns.map((col) => ({
        ...col,
        valueGetter: () => null,
        onCellClicked: undefined,
        editable: false,
        // Draws a bar for every row, loaded or placeholder: in the swapping phase
        // these columns sit over real rows, and a `__skeleton` check would
        // blank those cells for a frame.
        cellRenderer: () => {
          // The pinned expand column is a narrow chevron affordance — leave it
          // empty so it only reserves its width.
          if (col.colId === EXPAND_COLUMN_ID) {
            return null;
          }
          return <SkeletonCell />;
        },
      })),
    [finalColumns],
  );

  // Live mirror of the user's visibility toggles. A ref rather than a memo dep
  // so mid-life toggles don't hand AG Grid a fresh column array — it treats one
  // as a structural change and rebuilds sort indicators and column widths.
  const visibleColumnIdsRef = React.useRef(visibleColumnIds);
  visibleColumnIdsRef.current = visibleColumnIds;

  // Overlay the user's toggles onto ColDef `hide` before a column array reaches
  // the grid. AG Grid fires no `columnVisible` for initial ColDef application,
  // so an array applied as-is reverts the grid to ColDef defaults while the
  // picker (driven by `visibleColumnIds`) keeps the user's toggles. Consumers
  // with no visibility seed (`initialVisibleColumnIds === null`) stay
  // ColDef-driven: their `visibleColumnIds` mirror starts empty and would
  // otherwise hide everything.
  const reconcileVisibility = React.useCallback(
    (base: ColDef<ExpandableGridRow<TData>>[]) => {
      if (initialVisibleColumnIds === null) {
        return base;
      }
      const visible = visibleColumnIdsRef.current;
      return base.map((col) => {
        const id = colId(col);
        // `lockVisible` columns are always-on by contract (e.g. a pinned
        // actions column) and deliberately sit outside the picker, so they
        // never appear in `visibleColumnIds`. Honor their own ColDef `hide`
        // instead of forcing them hidden — same exemption as AG Grid's
        // internal selection/expand columns.
        if (INTERNAL_COLUMN_IDS.has(id) || col.lockVisible) {
          return col;
        }
        return { ...col, hide: !visible.has(id) };
      });
    },
    [initialVisibleColumnIds],
  );

  // Reconciled on each crossing of the skeleton/loaded boundary rather than
  // once per array: a sort or filter refetch crosses it, and an array
  // reconciled earlier would carry the visibility from before the user's
  // latest toggles and hide a column they had just shown.
  const gridColumns = React.useMemo(
    () =>
      reconcileVisibility(columnsAreLoaded ? finalColumns : skeletonColumns),
    [reconcileVisibility, columnsAreLoaded, finalColumns, skeletonColumns],
  );

  const gridRows: ExpandableGridRow<TData>[] =
    phase === "skeleton" ? skeletonRows : loadedRows;

  // Advance one phase per commit toward what `loading` asks for.
  React.useEffect(
    function walkTowardTarget() {
      const target: LoadingPhase = showSkeleton ? "skeleton" : "loaded";
      const next = stepToward(phase, target);
      if (next !== phase) {
        setPhase(next);
      }
    },
    [showSkeleton, phase],
  );

  const handleColumnMoved = React.useCallback(
    (event: ColumnMovedEvent<ExpandableGridRow<TData>>) => {
      if (!event.finished) {
        return;
      }
      // AG Grid also fires columnMoved while hydrating `initialState.columnOrder`.
      // This only invokes the callback on user-initiated re-ordering.
      if (
        event.source !== "uiColumnDragged" &&
        event.source !== "uiColumnMoved"
      ) {
        return;
      }
      const ordered = (event.api.getAllDisplayedColumns() ?? [])
        .map((c) => c.getColId())
        .filter((id) => !INTERNAL_COLUMN_IDS.has(id));
      onColumnOrderChange(ordered);
    },
    [onColumnOrderChange],
  );

  const handleColumnVisible = React.useCallback(
    (event: ColumnVisibleEvent<ExpandableGridRow<TData>>) => {
      const id = event.column?.getColId();
      if (!id) {
        return;
      }
      onVisibleColumnChange(id, event.visible ?? false);
    },
    [onVisibleColumnChange],
  );

  const handleSortChanged = React.useCallback(
    (event: SortChangedEvent<ExpandableGridRow<TData>>) => {
      if (event.source !== "uiColumnSorted") {
        return;
      }

      const active = event.api
        .getColumnState()
        .find((c) => c.sort !== null && c.sort !== undefined && c.colId);
      if (!active?.colId) {
        setSort(null);
        return;
      }
      setSort({
        columnId: active.colId,
        direction: active.sort === "asc" ? "asc" : "desc",
      });
    },
    [setSort],
  );

  const rowSelectionConfig = React.useMemo(() => {
    if (!selectionEnabled) {
      return undefined;
    }
    // Detail panels and loading placeholders are never selectable, so a
    // select-all can't pull them into the selection. AG Grid still renders a
    // (disabled) checkbox for non-selectable rows — that's fine; the skeleton
    // rows just show an inert checkbox alongside their placeholder bars.
    return {
      ...ROW_SELECTION_BASE,
      isRowSelectable: (node: { data?: ExpandableGridRow<TData> }) =>
        !node.data?.__detail && !node.data?.__skeleton,
    } as const;
  }, [selectionEnabled]);

  // Re-hydrate AG Grid's per-node selection from the hook's cross-page
  // accumulator whenever `data` shifts (e.g. paginating back to a page
  // that had selections).
  //
  // AG Grid evicts nodes that leave the row model,
  // so without this an already-selected row reappears unchecked.
  React.useEffect(() => {
    if (!selectionEnabled) {
      return;
    }
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    api.forEachNode((node) => {
      const nodeData = node.data;
      if (!nodeData || nodeData.__detail) {
        return;
      }
      const id = stableGetRowId(nodeData);
      const shouldBeSelected = selectionMap.has(id);
      if (node.isSelected() !== shouldBeSelected) {
        node.setSelected(shouldBeSelected);
      }
    });
  }, [data, selectionMap, selectionEnabled, stableGetRowId]);

  const handleRowSelected = React.useCallback(
    (event: RowSelectedEvent<ExpandableGridRow<TData>>) => {
      if (!PER_ROW_SELECTION_SOURCES.has(event.source)) {
        return;
      }
      const row = event.data;
      if (!row || row.__detail) {
        return;
      }
      const consumerRow = row as TData;
      onRowSelectionChange(
        consumerRow,
        stableGetRowId(consumerRow),
        event.node.isSelected() ?? false,
      );
    },
    [onRowSelectionChange, stableGetRowId],
  );

  const handleSelectionChanged = React.useCallback(
    (event: SelectionChangedEvent<ExpandableGridRow<TData>>) => {
      if (!BULK_SELECTION_SOURCES.has(event.source)) {
        return;
      }
      const selectedRows = event.api
        .getSelectedRows()
        .filter((row) => !row.__detail) as TData[];
      // Walk currently-displayed nodes to recover the set of ids the
      // bulk op touched. Ids outside this set are offscreen and must be
      // preserved (so a current-page deselect-all doesn't drop selections
      // on prior pages).
      const affectedIds = new Set<string>();
      event.api.forEachNode((node) => {
        const nodeData = node.data;
        if (nodeData && !nodeData.__detail) {
          affectedIds.add(stableGetRowId(nodeData));
        }
      });
      onBulkSelectionChange(selectedRows, affectedIds);
    },
    [onBulkSelectionChange, stableGetRowId],
  );

  const fullWidthCellRenderer = React.useMemo(() => {
    if (!showExpansion || !renderExpandedRow) {
      return undefined;
    }
    const render = renderExpandedRow;
    return function FullWidthDetail(
      params: ICellRendererParams<ExpandableGridRow<TData>>,
    ) {
      const data = params.data;
      if (!data) {
        return null;
      }
      const { __detail: _detail, __expanded: _expanded, ...rest } = data;
      const consumerRow = rest as TData;
      return (
        <ExpandableDetailPanel
          cellParams={params as ICellRendererParams<unknown>}
          id={detailPanelId(stableGetRowId(consumerRow))}
        >
          {render(consumerRow)}
        </ExpandableDetailPanel>
      );
    };
  }, [showExpansion, renderExpandedRow, stableGetRowId]);

  const isFullWidthRow = React.useCallback(
    (params: IsFullWidthRowParams<ExpandableGridRow<TData>>) =>
      params.rowNode.data?.__detail === true,
    [],
  );

  const getRowClass = React.useCallback(
    (params: RowClassParams<ExpandableGridRow<TData>>) => {
      // Only the detail panel needs a row class.
      return params.data?.__detail ? styles.detailRow : "";
    },
    [],
  );

  const showEmptyState = !isLoading && data.length === 0;
  const showPagination = !(
    hidePaginationWhenShort &&
    totalRowCount !== undefined &&
    totalRowCount <= Math.min(...DEFAULT_PAGE_SIZE_OPTIONS)
  );

  return (
    <AgGridProvider modules={AG_GRID_MODULES}>
      <div className={classNames(styles.frame, className)}>
        <div className={styles.gridFrame}>
          <div
            className={styles.grid}
            role={ariaLabel === undefined ? undefined : "region"}
            aria-label={ariaLabel}
            aria-busy={phase !== "loaded"}
          >
            <AgGridReact<ExpandableGridRow<TData>>
              ref={gridRef}
              theme={themeQuartz}
              rowData={gridRows}
              columnDefs={gridColumns}
              defaultColDef={mergedDefaultColDef}
              initialState={initialGridState}
              getRowId={wrappedGetRowId}
              getRowClass={getRowClass}
              isFullWidthRow={showExpansion ? isFullWidthRow : undefined}
              fullWidthCellRenderer={fullWidthCellRenderer}
              rowSelection={rowSelectionConfig}
              selectionColumnDef={SELECTION_COLUMN_DEF}
              // Off while skeleton columns are showing: text selection forces
              // an `.ag-cell-wrapper` whose full-height `.ag-cell-value` span
              // breaks vertical centering of the placeholder bar. Tracks the
              // the current phase rather than `loading`, because skeleton
              // columns are still the ones rendering while swapping.
              enableCellTextSelection={columnsAreLoaded}
              // Cell renderers in this codebase routinely close over
              // async-resolving external data (e.g. lookup maps from
              // TanStack Query). Column virtualization defers mounting
              // off-screen cells until they scroll into view, so a
              // `refreshCells` issued while a column is off-screen
              // doesn't fire its renderer — the cell later mounts with
              // whatever the renderer last produced from a stale
              // closure. Tables in this app have small column counts
              // (~10), so the virtualization savings are negligible
              // compared to the lifecycle correctness cost. Disable
              // globally.
              suppressColumnVirtualisation
              // The ColDef array sets the order columns start in; after that
              // the grid owns it. Without this, every new `columnDefs` array
              // — including the skeleton swap on each refetch — snaps columns
              // the user dragged back to ColDef order.
              maintainColumnOrder
              suppressColumnMoveAnimation
              suppressMultiSort
              // Don't animate the loading rows because it results in a flash
              animateRows={showSkeleton === false}
              onSortChanged={handleSortChanged}
              onColumnMoved={handleColumnMoved}
              onColumnVisible={handleColumnVisible}
              onRowSelected={selectionEnabled ? handleRowSelected : undefined}
              onSelectionChanged={
                selectionEnabled ? handleSelectionChanged : undefined
              }
              suppressOverlays={SUPPRESSED_OVERLAYS}
            />
          </div>
          {showEmptyState ? (
            <div className={styles.emptyOverlay}>{noRowsContent}</div>
          ) : null}
        </div>
        {showPagination && (
          <PaginationFooter
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={prevPage}
            hasNextPage={hasNextPage}
            canGetPrevPage={canGetPrevPage}
            totalRowCount={totalRowCount}
            loading={loading}
          />
        )}
      </div>
    </AgGridProvider>
  );
}
